import { Menu } from "antd";
import type { MenuProps } from "antd";
import logo from "assets/images/logo.png";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { Route } from "router";
import { permissionStore } from "store/permissionStore";
import { userStore } from "store/userStore";
import { can } from "utils/auth";
import "../styles/Sidebar.scss";

export const SIDENAV_WIDTH = 245;

const canAccess = (route: Route) => !!route.isPublic || can(route.path || "", "VIEW");

type MenuItems = NonNullable<MenuProps["items"]>;

// Nhãn là đường dẫn thật để bấm chuột giữa / Ctrl+click mở được tab mới
const menuLink = (path: string, title?: string) => <Link to={path}>{title}</Link>;

const buildRouteItems = (route: Route): MenuItems => {
  const children = route.children?.filter(
    (child) => !child.hidden && child.element !== null && canAccess({ ...child, path: `${route.path}/${child.path}` }),
  );

  if (route.children?.length && !children?.length) {
    return [];
  }

  if (children?.length === 1) {
    const child = children[0];
    return [
      {
        key: `${route.path}/${child.path}`,
        icon: route.icon,
        label: menuLink(`${route.path}/${child.path}`, route.title),
      },
    ];
  }

  if (children?.length) {
    return [
      {
        key: route.path!,
        icon: route.icon,
        label: route.title,
        children: children.map((item) => ({
          key: `${route.path}/${item.path}`,
          icon: item.icon,
          label: menuLink(`${route.path}/${item.path}`, item.title),
        })),
      },
    ];
  }

  return [
    {
      key: route.path!,
      icon: route.icon,
      label: menuLink(route.path!, route.title),
    },
  ];
};

const groupKey = (group: string) => `group:${group}`;

const buildMenuItems = (routes: Route[]): MenuProps["items"] => {
  const visibleRoutes = routes.filter(
    (route) =>
      (route.checkIsDev ? userStore.info.isDev : true) &&
      !route.hidden &&
      !route.hideInMenu &&
      canAccess(route),
  );

  const groupedItems = new Map<string, MenuItems>();
  for (const route of visibleRoutes) {
    if (route.group) {
      groupedItems.set(route.group, [...(groupedItems.get(route.group) || []), ...buildRouteItems(route)]);
    }
  }

  const items: MenuItems = [];
  const emittedGroups = new Set<string>();
  for (const route of visibleRoutes) {
    if (!route.group) {
      items.push(...buildRouteItems(route));
      continue;
    }
    if (emittedGroups.has(route.group)) continue;
    emittedGroups.add(route.group);

    const groupChildren = groupedItems.get(route.group) || [];
    if (groupChildren.length === 1) {
      items.push(groupChildren[0]);
    } else if (groupChildren.length > 1) {
      items.push({ key: groupKey(route.group), icon: route.icon, label: route.group, children: groupChildren } as MenuItems[number]);
    }
  }

  return items;
};

const useAdminMenuItems = () => {
  return useMemo(
    () => buildMenuItems(permissionStore.accessRoutes),
    [permissionStore.accessRoutes, userStore.info.isDev, userStore.info.role?.permissions],
  );
};

const resolveMenuKey = (pathname: string) => {
  if (/^\/quote\/\d+$/.test(pathname)) {
    return "/quote";
  }
  if (pathname === "/opportunity/create" || pathname.startsWith("/opportunity/")) {
    return "/opportunity";
  }
  return pathname;
};

type SidebarMenuProps = {
  onNavigate?: () => void;
};

export const SidebarMenu = observer(({ onNavigate }: SidebarMenuProps) => {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const menuItems = useAdminMenuItems();

  useEffect(() => {
    const routes = permissionStore.accessRoutes;
    const parentRoute = routes.find(
      (route) => route.children && route.path && location.pathname.startsWith(route.path),
    );

    const groupRoute = routes.find((route) => route.group && route.path === location.pathname);

    if (parentRoute?.path) {
      setOpenKeys([parentRoute.path]);
    } else if (groupRoute?.group) {
      setOpenKeys([groupKey(groupRoute.group)]);
    }

    setIsLoaded(true);
  }, [location.pathname]);

  const handleOpenChange: MenuProps["onOpenChange"] = (keys) => {
    setOpenKeys(keys as string[]);
  };

  const handleMenuClick: MenuProps["onClick"] = () => {
    onNavigate?.();
  };

  return (
    <div className="admin-sidebar__inner">
      <div className="logo-box sticky top-0 flex items-center justify-center px-6 py-3 backdrop-blur-xs">
        <Link to="/" onClick={onNavigate}>
          <img src={logo} className="logo-lg" alt="Logo" />
        </Link>
      </div>

      {isLoaded && (
        <Menu
          className="sidebar"
          theme="light"
          mode="inline"
          selectedKeys={[resolveMenuKey(location.pathname)]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          onClick={handleMenuClick}
          items={menuItems}
        />
      )}
    </div>
  );
});

export const TopNavMenu = observer(() => {
  const location = useLocation();
  const menuItems = useAdminMenuItems();

  return (
    <Menu
      className="admin-top-menu"
      mode="horizontal"
      disabledOverflow
      theme="light"
      selectedKeys={[resolveMenuKey(location.pathname)]}
      items={menuItems}
    />
  );
});
